import type { RefPosition } from './index'
import { isYoungDivision } from './refereeWindows'

/** What every 6U/7U/8U slot is called, whatever its stored position.
 *
 *  ⚠️ Owner ruling 2026-09-08: CR and AR are a 10U+ distinction. A young game
 *  has four interchangeable officials, not a crew with a center, so naming one
 *  of them a center would assert a hierarchy the age group does not have.
 *  Happily this introduces no new vocabulary: at 10U+ `CR` already reads
 *  "Referee". */
export const POSITION_LABEL_YOUNG = 'Referee'

const COMPETITIVE_LABEL: Record<RefPosition, string> = {
  CR: 'Referee',
  AR1: 'Assistant Referee 1',
  AR2: 'Assistant Referee 2',
  // Unreachable: 10U+ duty derivation never emits an AR3. Present because the
  // record must be total, and a crash here would be a blank email.
  AR3: 'Assistant Referee 3',
}

const REF_POSITIONS: readonly string[] = ['CR', 'AR1', 'AR2', 'AR3'] satisfies readonly RefPosition[]

/** PURE. Narrows a stored position string to a `RefPosition`. */
export function isRefPosition(p: string): p is RefPosition {
  return REF_POSITIONS.includes(p)
}

/** The position labels in the order a crew reads: "Referee" first, then the
 *  assistants. The Role facet and the Role sort both rank by this, so the two
 *  agree on where a young game's AR2 (which reads "Referee") belongs. */
export const POSITION_LABEL_ORDER: readonly string[] = (['CR', 'AR1', 'AR2', 'AR3'] as const).map((p) => COMPETITIVE_LABEL[p])

/** PURE. A label's place in `POSITION_LABEL_ORDER`; an unknown label sorts last. */
export function positionLabelRank(label: string): number {
  const i = POSITION_LABEL_ORDER.indexOf(label)
  return i === -1 ? POSITION_LABEL_ORDER.length : i
}

/** PURE. The one display name for a slot's position, everywhere a person reads
 *  one: the SPA rows, the ref-admin overview, the Thursday nudge, the assignment
 *  alert, and the calendar event title.
 *
 *  ⚠️ THIS LIVES IN shared-types BECAUSE TWO PACKAGES NEED IT. It used to be
 *  SPA-only (`apps/referee-spa/src/lib/slotDisplay.ts`), and the referee Worker
 *  did not use it at all — it interpolated the RAW ENUM into emails
 *  (`positions.join(', ')` → "CR, AR1") and into the calendar title
 *  (`Referee ${slot.position}` → "Referee CR"). Both were merely ugly at 10U+
 *  and are wrong at 6U/7U/8U.
 *
 *  `ageGroup` is `referee_slots.age_group`, i.e. a division NAME like "B8".
 *  A division whose age cannot be resolved is treated as NOT young, matching
 *  `isYoungDivision`'s own "when in doubt, show the competitive UI" default. */
export function positionLabelFor(ageGroup: string, position: RefPosition): string {
  return isYoungDivision(ageGroup, null) ? POSITION_LABEL_YOUNG : COMPETITIVE_LABEL[position]
}

/** The position label a slot is shown as, optional marker included.
 *
 *  ⚠️ THE SUFFIX IS PART OF THE LABEL, NOT A CALLER'S GARNISH. Four surfaces
 *  each appended `(optional)` themselves, and the crew view added a fifth that
 *  FORGOT to — so the same 8U slot read "Referee (optional)" on My teams and
 *  plain "Referee" on My games, for one row of one game. Anything rendering a
 *  slot's position wants this, not `positionLabelFor`. */
export function slotPositionLabel(
  ageGroup: string,
  position: RefPosition,
  isOptional: boolean,
): string {
  const label = positionLabelFor(ageGroup, position)
  return isOptional ? `${label} (optional)` : label
}
